export type HighlightChunk = {
  isHighlighted: boolean
  text: string
}

function* highlightedChunksGenerator(
  text: string,
  search: string,
  caseSensitive = false,
  ignoreChars: string[] = [],
): Generator<HighlightChunk> {
  if (!search) {
    yield { isHighlighted: false, text }
    return
  }

  const ignoreSet = new Set(ignoreChars)

  // Step 1: Normalize the source text by removing ignored characters
  // At the same time, build a map of original indices to be able to map back
  const normMap: number[] = [] // Maps normalized index to original index
  const normChars: string[] = [] // Normalized characters of text
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (!ignoreSet.has(char)) {
      normMap.push(i)
      normChars.push(caseSensitive ? char : char.toLowerCase())
    }
  }
  const normalizedText = normChars.join('')

  // Step 2: Normalize the search query using the same ignore rules
  const searchChars: string[] = []
  for (let i = 0; i < search.length; i++) {
    const char = search[i]
    if (!ignoreSet.has(char)) {
      searchChars.push(caseSensitive ? char : char.toLowerCase())
    }
  }
  const normalizedSearch = searchChars.join('')

  // If the normalized search query is empty after removing ignored characters, return full unhighlighted text
  if (!normalizedSearch) {
    yield { isHighlighted: false, text }
    return
  }

  // Step 3: Find all non-overlapping matches of normalized search in normalized text
  const matchRanges: Array<{ start: number; end: number }> = []
  let fromIndex = 0
  while (fromIndex <= normMap.length - normalizedSearch.length) {
    const idx = normalizedText.indexOf(normalizedSearch, fromIndex)
    if (idx === -1) break
    // Map the normalized match back to the original text indices
    matchRanges.push({
      start: normMap[idx],
      end: normMap[idx + normalizedSearch.length - 1],
    })
    fromIndex = idx + normalizedSearch.length // Skip past this match (no overlaps)
  }

  // Step 4: Generate highlighted and non-highlighted chunks based on match ranges
  let lastIdx = 0
  for (const { start, end } of matchRanges) {
    if (start > lastIdx) {
      // Yield the plain chunk before match
      yield { isHighlighted: false, text: text.slice(lastIdx, start) }
    }
    // Yield the highlighted match
    yield { isHighlighted: true, text: text.slice(start, end + 1) }
    lastIdx = end + 1
  }

  // Step 5: Yield any remaining text after the last match
  if (lastIdx < text.length) {
    yield { isHighlighted: false, text: text.slice(lastIdx) }
  }
}

export const getHighlightedChunksForText = (
  text: string,
  search: string,
  caseSensitive = false,
  ignoreChars: string[] = [],
): HighlightChunk[] => Array.from(highlightedChunksGenerator(text, search, caseSensitive, ignoreChars))
